import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'
import LocalStorageService from 'src/services/LocalStorageService'
import { selectCurrentUserId } from 'src/features/account/duck/selectors'
import { getOfferSnapshot, OFFER_ENDPOINTS } from './offerHistory'
import {
  getAccountOfferStorageKey,
  removeLegacyOfferStorage
} from './offerStorage'

export const OFFER_FAVORITES_STORAGE_KEY = 'exchangeOfferFavorites'
export const OFFER_FAVORITES_CHANGED_EVENT = 'exchangeOfferFavoritesChanged'

const OFFER_TIME_KEYS = {
  export: 'loadingTime',
  import: 'pickupTime',
  general: 'loadingTime',
  container: 'availabilityTime',
  truck: 'availabilityTime'
}

const getFavoriteKey = offer => `${offer.offerType}:${offer.id}`

const getRawValue = (offer, key) => {
  if (!key) return ''
  if (offer.raw && offer.raw[key] !== undefined && offer.raw[key] !== null) {
    return offer.raw[key]
  }
  return offer[key]
}

const serializeLocation = location => {
  if (!location) return null
  return {
    id: location.id,
    name: location.name || '',
    postalCode: location.postalCode || '',
    countryCode: location.countryCode || '',
    lat: Number(location.lat),
    long: Number(location.long)
  }
}

const getOfferExpiration = offer => {
  const endpointKeys = OFFER_ENDPOINTS[offer.offerType]
  if (!endpointKeys) return null

  const dateValue = getRawValue(offer, endpointKeys[2])
  if (!dateValue) return null

  const date = moment(dateValue)
  if (!date.isValid()) return null

  const timeValue = getRawValue(offer, OFFER_TIME_KEYS[offer.offerType])
  const timeMatch = String(timeValue || '').match(/^(\d{1,2}):(\d{2})/)
  if (timeMatch) {
    date
      .hour(Number(timeMatch[1]))
      .minute(Number(timeMatch[2]))
      .second(59)
      .millisecond(999)
  } else {
    date.endOf('day')
  }

  return Math.max(
    date.valueOf(),
    moment()
      .endOf('day')
      .valueOf()
  )
}

const getFavoriteSnapshot = offer => {
  const endpointKeys = OFFER_ENDPOINTS[offer.offerType]
  const [fromLocationKey, toLocationKey] = endpointKeys

  return {
    id: offer.id,
    offerType: offer.offerType,
    referenceNumber: offer.referenceNumber,
    ...getOfferSnapshot(offer),
    fromLocation: serializeLocation(offer[fromLocationKey]),
    toLocation: serializeLocation(offer[toLocationKey]),
    fromTime: getRawValue(offer, OFFER_TIME_KEYS[offer.offerType]) || '',
    isReservable: Boolean(offer.isReservable),
    reservationsCount: Number(offer.reservationsCount) || 0,
    type: offer.type || null,
    expiresAt: getOfferExpiration(offer),
    favoritedAt: Date.now()
  }
}

const saveOfferFavorites = (favorites, userId) => {
  const storageKey = getAccountOfferStorageKey(
    OFFER_FAVORITES_STORAGE_KEY,
    userId
  )
  if (!storageKey) return false

  try {
    LocalStorageService.set(storageKey, JSON.stringify(favorites))
  } catch (error) {
    return false
  }
  return true
}

export const getOfferFavorites = userId => {
  const storageKey = getAccountOfferStorageKey(
    OFFER_FAVORITES_STORAGE_KEY,
    userId
  )
  if (!storageKey) return []

  try {
    removeLegacyOfferStorage(OFFER_FAVORITES_STORAGE_KEY)
    const storedFavorites = JSON.parse(
      LocalStorageService.get(storageKey) || '[]'
    )
    if (!Array.isArray(storedFavorites)) return []

    const now = Date.now()
    const activeFavorites = storedFavorites.filter(
      favorite =>
        favorite.isReservable !== false ||
        !favorite.expiresAt ||
        Number(favorite.expiresAt) >= now
    )
    if (activeFavorites.length !== storedFavorites.length) {
      saveOfferFavorites(activeFavorites, userId)
    }
    return activeFavorites
  } catch (error) {
    return []
  }
}

const notifyFavoritesChanged = (favorites, userId) => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent(OFFER_FAVORITES_CHANGED_EVENT, {
      detail: { favorites, userId }
    })
  )
}

export const useOfferFavorites = () => {
  const userId = useSelector(selectCurrentUserId)
  const storageKey = getAccountOfferStorageKey(
    OFFER_FAVORITES_STORAGE_KEY,
    userId
  )
  const [favoritesState, setFavoritesState] = useState({
    userId: null,
    favorites: []
  })
  const favorites =
    favoritesState.userId === userId ? favoritesState.favorites : []

  useEffect(() => {
    const refreshFavorites = event => {
      if (event && event.detail && event.detail.userId !== userId) return
      setFavoritesState({
        userId,
        favorites:
          event && event.detail && Array.isArray(event.detail.favorites)
            ? event.detail.favorites
            : getOfferFavorites(userId)
      })
    }
    const handleStorage = event => {
      if (event.key === storageKey) refreshFavorites()
    }

    refreshFavorites()
    window.addEventListener(OFFER_FAVORITES_CHANGED_EVENT, refreshFavorites)
    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener(
        OFFER_FAVORITES_CHANGED_EVENT,
        refreshFavorites
      )
      window.removeEventListener('storage', handleStorage)
    }
  }, [storageKey, userId])

  useEffect(() => {
    const nextExpiration = favorites.reduce((nearestExpiration, favorite) => {
      if (favorite.isReservable !== false) return nearestExpiration
      const expiration = Number(favorite.expiresAt)
      if (!Number.isFinite(expiration) || expiration < Date.now()) {
        return nearestExpiration
      }
      return nearestExpiration === null || expiration < nearestExpiration
        ? expiration
        : nearestExpiration
    }, null)
    if (nextExpiration === null) return undefined

    const expirationTimeout = window.setTimeout(() => {
      setFavoritesState({ userId, favorites: getOfferFavorites(userId) })
    }, Math.min(nextExpiration - Date.now() + 50, 2147483647))
    return () => window.clearTimeout(expirationTimeout)
  }, [favorites, userId])

  const favoriteKeys = useMemo(() => new Set(favorites.map(getFavoriteKey)), [
    favorites
  ])

  const isFavorite = useCallback(
    (offerType, id) => favoriteKeys.has(`${offerType}:${id}`),
    [favoriteKeys]
  )

  const removeFavorite = useCallback((offerType, id) => {
    if (!storageKey) return
    const favoriteKey = `${offerType}:${id}`
    const nextFavorites = getOfferFavorites(userId).filter(
      favorite => getFavoriteKey(favorite) !== favoriteKey
    )
    if (saveOfferFavorites(nextFavorites, userId)) {
      setFavoritesState({ userId, favorites: nextFavorites })
      notifyFavoritesChanged(nextFavorites, userId)
    }
  }, [storageKey, userId])

  const toggleFavorite = useCallback(offer => {
    if (
      !storageKey ||
      !offer ||
      !offer.id ||
      !offer.offerType ||
      !offer.referenceNumber
    ) {
      return
    }

    const currentFavorites = getOfferFavorites(userId)
    const favoriteKey = getFavoriteKey(offer)
    const alreadyFavorite = currentFavorites.some(
      favorite => getFavoriteKey(favorite) === favoriteKey
    )
    const favoriteSnapshot = alreadyFavorite ? null : getFavoriteSnapshot(offer)
    const nextFavorites = alreadyFavorite
      ? currentFavorites.filter(
          favorite => getFavoriteKey(favorite) !== favoriteKey
        )
      : [favoriteSnapshot, ...currentFavorites]

    if (saveOfferFavorites(nextFavorites, userId)) {
      setFavoritesState({ userId, favorites: nextFavorites })
      notifyFavoritesChanged(nextFavorites, userId)
    }
  }, [storageKey, userId])

  return { favorites, isFavorite, removeFavorite, toggleFavorite }
}
